import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { AuthApiService, SessionInfo } from '../../services/auth-api.service';
import { I18nService } from '../../services/i18n.service';
import { AuthCardComponent } from '../../ui/auth-card.component';

/**
 * Страница состояния SSO-сессии. Юзер пришёл напрямую на auth-server'е
 * и уже залогинен — показываем его email и две кнопки: «Перейти на сайт»
 * (выполняет continueUrl, который рассчитал backend) и «Выйти» (POST
 * /api/auth/logout, потом редиректим на форму /:lang/login). Если
 * сессии нет — сразу уезжаем на login.
 */
@Component({
  selector: 'auth-account',
  standalone: true,
  imports: [AuthCardComponent],
  template: `
    <auth-card [title]="i18n.t('account.heading')">
      @if (loading()) {
        <p class="au-card__sub">{{ i18n.t('account.loading') }}</p>
      } @else if (info(); as session) {
        <p class="au-card__sub">
          {{ i18n.t('account.subtitle') }} <strong>{{ session.email }}</strong>
        </p>
        <button type="button" class="au-button" (click)="continue(session)" [disabled]="busy()">
          {{ i18n.t('account.continue') }}
        </button>
        <button type="button" class="au-button au-button--ghost" (click)="logout()" [disabled]="busy()">
          {{ i18n.t('account.logout') }}
        </button>
      }
    </auth-card>
  `,
  styles: [`
    .au-button + .au-button { margin-top: 12px; }
    .au-button--ghost {
      background: transparent;
      color: var(--au-primary, #195db0);
      border: 1px solid var(--au-primary, #195db0);
    }
    .au-button--ghost:hover:not(:disabled) {
      background: var(--au-primary, #195db0);
      color: #fff;
    }
    .au-card__sub strong { color: var(--au-text, #1f2630); }
  `],
})
export class AccountComponent implements OnInit {
  private api = inject(AuthApiService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  protected i18n = inject(I18nService);

  protected langPath: 'ru' | 'kz' = 'ru';
  protected readonly loading = signal(true);
  protected readonly busy = signal(false);
  protected readonly info = signal<SessionInfo | null>(null);

  async ngOnInit(): Promise<void> {
    const langParam = this.route.snapshot.paramMap.get('lang');
    this.langPath = langParam === 'kz' ? 'kz' : 'ru';
    await this.i18n.use(I18nService.normaliseFromUrl(langParam));

    this.api.getSessionInfo().subscribe({
      next: (resp) => {
        this.loading.set(false);
        if (!resp.authenticated) {
          // Сессии нет — на форму логина (тот же URL без /account).
          this.router.navigate(['/', this.langPath, 'login']);
          return;
        }
        this.info.set(resp);
      },
      error: () => {
        this.loading.set(false);
        this.router.navigate(['/', this.langPath, 'login']);
      },
    });
  }

  continue(session: SessionInfo): void {
    if (!session.continueUrl) return;
    this.busy.set(true);
    window.location.assign(session.continueUrl);
  }

  logout(): void {
    this.busy.set(true);
    this.api.logout().subscribe({
      next: () => this.router.navigate(['/', this.langPath, 'login'], { queryParams: { logout: '1' } }),
      error: () => this.router.navigate(['/', this.langPath, 'login'], { queryParams: { logout: '1' } }),
    });
  }
}
